import { useEffect, useRef, useState } from "react";
import Portal from "@Components/Portal";
import useModal from "@Components/Modal/useModal";
import { useLocation } from "react-router";
import CategoryRatingBar from "../CategoryRatingBar";
import PhotoGallery from "../PhotoGallery";
import ReviewCard from "../ReviewCard";
import VerifiedBadge from "../VerifiedBadge";
import Tooltip from "@Components/Tooltip/Tooltip";
import useTranslationHook from "hooks/useTranslationHook";
import { ReviewData, CategoryRating, PhotoItem } from "../../types";
import styles from "./reviewsPanel.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";

interface ReviewsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  overallRating: number;
  ratingLabel: string;
  reviewCount: number;
  categories: CategoryRating[];
  summary: string;
  photos: PhotoItem[];
  reviews: ReviewData[];
  onMediaClick?: (index: number) => void;
  scrollToReviewId?: string | null;
  mediaIndexMap?: Map<string, number>;
  history: any;
}

const STAGGER_DELAY = 0.03;
const ANIMATION_DURATION = 0.5;
const INITIAL_DELAY = 0.2;

const ReviewsPanel: React.FC<ReviewsPanelProps> = ({
  isOpen,
  onClose,
  overallRating,
  ratingLabel,
  reviewCount,
  categories,
  summary,
  photos,
  reviews,
  onMediaClick,
  scrollToReviewId,
  mediaIndexMap,
  history,
}) => {
  const panelContentRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslationHook();
  const location = useLocation();
  const [animateIn, setAnimateIn] = useState(false);

  const summaryWords = summary ? summary.split(" ") : [];

  useModal(history, location, "ReviewsPanel", onClose);

  // Trigger animations after panel opens
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setAnimateIn(true), 300); // After slide-in completes
      return () => clearTimeout(timer);
    }
    setAnimateIn(false);
    return undefined;
  }, [isOpen]);
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !scrollToReviewId || !panelContentRef.current) return undefined;
    const timer = setTimeout(() => {
      const el = panelContentRef.current?.querySelector(`[data-review-id="${scrollToReviewId}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => clearTimeout(timer);
  }, [isOpen, scrollToReviewId]);

  if (!isOpen) return null;

  return (
    <Portal>
      <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="All Reviews">
        <div
          className={styles.backdrop}
          onClick={onClose}
          onTouchMove={(e) => e.preventDefault()}
          onKeyDown={(e) => {
            if (e.key === "Enter") onClose();
          }}
          role="button"
          tabIndex={0}
          aria-label="Close reviews panel"
        />

        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <span className={styles.panelTitle}>{t("propertypage.reviews.title")}</span>
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="#111928"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className={styles.panelContent} ref={panelContentRef}>
            <div className={styles.sectionRatingOuter}>
              <div className={styles.ratingSection}>
                <div className={styles.scoreColumn}>
                  <div className={styles.scoreRow}>
                    <div className={styles.scoreBadge}>
                      <span className={styles.scoreNumber}>{overallRating.toFixed(1)}</span>
                      <span className={styles.scoreMax}>/5</span>
                    </div>
                    <div className={styles.scoreInfo}>
                      <span className={styles.ratingLabel}>{t(ratingLabel)}</span>
                      {reviewCount > 0 && (
                        <span className={styles.reviewCount}>
                          {reviewCount} {t("propertypage.reviews.title")}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={styles.separator} />
                  <VerifiedBadge />
                </div>

                <div className={styles.categoryGrid}>
                  {categories.map((cat) => (
                    <CategoryRatingBar
                      key={cat.label}
                      label={cat.label}
                      labelKey={cat.labelKey}
                      score={cat.score}
                      animate={animateIn}
                    />
                  ))}
                </div>
              </div>

              <div className={styles.summarySection}>
                <p className={styles.summaryText}>
                  {summaryWords.map((word, idx) => (
                    <span
                      key={idx}
                      className={`${styles.revealWord} ${
                        animateIn ? styles.revealWordVisible : ""
                      }`}
                      style={{
                        transitionDelay: animateIn
                          ? `${INITIAL_DELAY + idx * STAGGER_DELAY}s`
                          : "0s",
                        transitionDuration: `${ANIMATION_DURATION}s`,
                      }}
                    >
                      {word}
                      {idx < summaryWords.length - 1 ? "\u00A0" : ""}
                    </span>
                  ))}
                </p>
                <div
                  className={`${styles.summarySource} ${
                    animateIn ? styles.summarySourceVisible : ""
                  }`}
                  style={{
                    transitionDelay: animateIn
                      ? `${INITIAL_DELAY + summaryWords.length * STAGGER_DELAY + 0.3}s`
                      : "0s",
                  }}
                >
                  <span className={styles.summarySourceText}>
                    {t("propertypage.reviews.summarysource")}
                  </span>
                  <Tooltip
                    content={t("propertypage.reviews.aisummarytooltip")}
                    direction="top"
                    className={styles.tooltip}
                    classNameWrapper={styles.tooltipWrapper}
                    addComponent
                    delay={0}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="7.5" stroke="#6B7280" />
                      <path d="M8 7V11" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" />
                      <circle cx="8" cy="5" r="0.75" fill="#6B7280" />
                    </svg>
                  </Tooltip>
                </div>
              </div>

              {photos.length > 0 && (
                <div className={styles.galleryWrapper}>
                  <PhotoGallery photos={photos} onMediaClick={onMediaClick} />
                </div>
              )}
              <div className={styles.panelDivider} />
            </div>

            <div className={styles.reviewsList}>
              {reviews.map((review, idx) => (
                <div
                  key={review.id}
                  data-review-id={review.id}
                  className={`${styles.reviewCardAnimated} ${
                    animateIn ? styles.reviewCardVisible : ""
                  }`}
                  style={{
                    transitionDelay: animateIn ? `${0.4 + idx * 0.08}s` : "0s",
                  }}
                >
                  <ReviewCard
                    review={review}
                    onMediaClick={onMediaClick}
                    mediaStartIndex={mediaIndexMap?.get(review.id) || 0}
                    fullWidth
                    showFullBody
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default wrapperHOC(ReviewsPanel, {
  componentName: "ReviewsPanel",
  showForChina: true,
});
